import * as React from "react";

import type { LoginHistoryEntry } from "api/settings";
import { Badge, EmptyState, Skeleton } from "Components/ui";
import { Globe, History } from "Components/ui/icons";
import { formatDateTime } from "utils/format";

interface LoginHistoryProps {
  entries: LoginHistoryEntry[];
  loading: boolean;
  /** Shown when an entry carries no user name (it's usually your own history). */
  fallbackUserName?: string;
}

type LoginLocation = LoginHistoryEntry["location"];

const formatIp = (ip?: string | null): string | null => {
  const trimmed = ip?.trim();
  if (!trimmed) return null;
  const stripped = trimmed.replace(/^::ffff:/, "");
  return stripped === "::1" || stripped === "127.0.0.1" ? "Localhost" : stripped;
};

const formatLocation = (location: LoginLocation): string | null => {
  if (!location) return null;
  if (typeof location === "string") return location.trim() || null;
  const parts = [location.label, location.city, location.region, location.country].filter(Boolean);
  return parts.length ? parts.join(", ") : null;
};

const isFailed = (entry: LoginHistoryEntry) =>
  entry.status?.toLowerCase() === "failed" ||
  entry.success === false ||
  (!entry.ip && entry.success !== true);

const displayName = (entry: LoginHistoryEntry, fallback?: string) =>
  entry.username || entry.user?.name || entry.user?.userName || entry.user?.email || fallback;

/** Sign-in records, newest first: who, from where, and whether it worked. */
const LoginHistory: React.FC<LoginHistoryProps> = ({ entries, loading, fallbackUserName }) => {
  const sorted = React.useMemo(
    () =>
      [...entries].sort(
        (a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
      ),
    [entries]
  );

  if (loading) {
    return (
      <div className="space-y-3" aria-busy="true">
        {[0, 1, 2, 3].map((i) => (
          <Skeleton key={i} className="h-16" />
        ))}
      </div>
    );
  }

  if (sorted.length === 0) {
    return <EmptyState compact icon={<History />} title="No sign-ins recorded yet" />;
  }

  return (
    <ul className="divide-y divide-border rounded-card border border-border bg-surface">
      {sorted.map((entry, index) => {
        const failed = isFailed(entry);
        const location = formatLocation(entry.location);
        const ip = formatIp(entry.ip);
        return (
          <li key={`${entry.timestamp}-${index}`} className="flex items-start gap-3 px-4 py-3">
            <span
              aria-hidden="true"
              className={
                failed
                  ? "mt-1.5 h-2 w-2 shrink-0 rounded-full bg-danger"
                  : "mt-1.5 h-2 w-2 shrink-0 rounded-full bg-success"
              }
            />
            <div className="min-w-0 flex-1 space-y-1">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <p className="truncate text-sm font-medium text-foreground">
                  {displayName(entry, fallbackUserName) || "Unknown user"}
                </p>
                <Badge tone={failed ? "danger" : "success"}>
                  {failed ? "Failed" : entry.status || "Signed in"}
                </Badge>
              </div>
              <p className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
                <span className="tabular-nums">{formatDateTime(entry.timestamp)}</span>
                <span className="inline-flex items-center gap-1">
                  <Globe aria-hidden="true" className="h-3.5 w-3.5" />
                  {location || "Location not detected"}
                </span>
                {ip && <span className="font-mono">{ip}</span>}
              </p>
            </div>
          </li>
        );
      })}
    </ul>
  );
};

export default LoginHistory;
